import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box, Typography, Skeleton, Button, Avatar, TextField, InputAdornment,
  Card, Chip, IconButton
} from "@mui/material";
import { Search, Users, ArrowLeft, UserPlus, Check, X, XCircle } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import { useSystems } from "../../../../context/SystemContext";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../DashboardLayout";
import axios from "axios";
import PersonHoverCard from "./widgets/PersonHoverCard";

const RELATIONS_API = "https://apiweb.hitpoly.com/ajax/RelacionesControlador.php";
const COMMUNITY_API = "https://apiweb.hitpoly.com/ajax/communityWidgetsController.php";
const AVATAR_CACHE = {};

const PeopleNetworkPage = ({ onBack }) => {
  const navigate = useNavigate();
  const backHandler = onBack ? onBack : () => navigate(-1);
  const { user } = useAuth();
  const { activeProfile } = useSystems();
  const profileType = activeProfile?.type;
  const [allPeople, setAllPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avatarStatus, setAvatarStatus] = useState({});
  const [relationStatuses, setRelationStatuses] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(50);
  const mountedRef = useRef(true);
  const userIdRef = useRef(null);

  const [hoverPerson, setHoverPerson] = useState(null);
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const hoverTimer = useRef(null);

  const handleMouseEnter = (e, person) => {
    clearTimeout(hoverTimer.current);
    const target = e.currentTarget;
    hoverTimer.current = setTimeout(() => {
      setHoverPerson(person);
      setHoverAnchor(target);
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setHoverPerson(null);
      setHoverAnchor(null);
    }, 300);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  userIdRef.current = user?.user_id || user?.id;

  const createSlug = useCallback((nombre, apellido) => {
    const cleanNombre = String(nombre || "").toLowerCase().trim().replace(/\s+/g, "");
    const cleanApellido = String(apellido || "").toLowerCase().trim().replace(/\s+/g, "");
    return cleanApellido ? `${cleanNombre}.${cleanApellido}` : cleanNombre;
  }, []);

  const goToProfile = useCallback((person) => {
    const nombre = person.nombre || person.name?.split(" ")[0] || "";
    const apellido = person.apellido || person.name?.split(" ").slice(1).join(" ") || "";
    const slug = createSlug(nombre, apellido);
    navigate(`/${slug}`, { state: { targetId: person.id } });
  }, [navigate, createSlug]);

  const checkAvatar = useCallback((url) => {
    if (!url) return Promise.resolve(false);
    if (url in AVATAR_CACHE) return Promise.resolve(AVATAR_CACHE[url]);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { AVATAR_CACHE[url] = true; resolve(true); };
      img.onerror = () => { AVATAR_CACHE[url] = false; resolve(false); };
      img.src = url;
    });
  }, []);

  const checkRelationStatus = useCallback(async (currentUserId, targetId) => {
    if (!currentUserId || !targetId) return { status: "none", isSender: false };
    try {
      const res = await axios.get(RELATIONS_API, {
        params: { action: "status", user_id: currentUserId, target_id: targetId }
      });
      if (res.data?.success) {
        return {
          status: res.data.status || "none",
          isSender: Number(res.data.remitente_original) === Number(currentUserId)
        };
      }
    } catch { }
    return { status: "none", isSender: false };
  }, []);

  const friendTimeouts = useRef({});

  const handleFriendAction = useCallback(async (person, action) => {
    const currentUserId = userIdRef.current;
    if (!currentUserId || !person.id) return;
    try {
      const res = await axios.post(RELATIONS_API, {
        user_id: currentUserId,
        target_id: person.id,
        action: action
      });
      if (res.data?.success && mountedRef.current) {
        setRelationStatuses(prev => ({ ...prev, [person.id]: { status: res.data.newStatus, isSender: action === "enviar" } }));
        if (action === "enviar") {
          if (friendTimeouts.current[person.id]) {
            clearTimeout(friendTimeouts.current[person.id]);
          }
          friendTimeouts.current[person.id] = setTimeout(() => {
            setAllPeople(prev => prev.filter(p => p.id !== person.id));
            delete friendTimeouts.current[person.id];
          }, 30000);
          axios.post("https://apiweb.hitpoly.com/ajax/NotificacionesDeSolicitudDeAmistadController.php", {
            user_id: currentUserId, target_id: person.id, tipo: "enviar"
          }).catch(() => { });
        }
        if (action === "eliminar" || action === "cancelar") {
          if (friendTimeouts.current[person.id]) {
            clearTimeout(friendTimeouts.current[person.id]);
            delete friendTimeouts.current[person.id];
          }
          setRelationStatuses(prev => ({
            ...prev,
            [person.id]: { status: res?.data?.newStatus || "none", isSender: false }
          }));
        }
      }
    } catch { }
  }, []);

  useEffect(() => () => {
    Object.values(friendTimeouts.current).forEach(clearTimeout);
    friendTimeouts.current = {};
  }, []);

  useEffect(() => {
    const currentUserId = user?.user_id || user?.id;
    if (!currentUserId) { setLoading(false); return; }

    let cancelled = false;
    const controller = new AbortController();
    const action = profileType === 'professional' ? 'getProfessionalProfiles' : 'getPeopleYouMayKnow';

    const fetchPeople = async () => {
      try {
        const response = await axios.post(COMMUNITY_API, { action, user_id: currentUserId }, { signal: controller.signal });
        if (cancelled) return;
        const items = response.data?.datos || response.data?.data || [];

        if (!items.length) {
          if (mountedRef.current) setLoading(false);
          return;
        }

        const statuses = {};
        await Promise.all(items.map(async (person) => {
          statuses[person.id] = person.avatar ? await checkAvatar(person.avatar) : false;
        }));

        if (cancelled) return;

        let withAvatar = items.filter(p => statuses[p.id]);
        let withoutAvatar = items.filter(p => !statuses[p.id]);

        const combined = [...withAvatar, ...withoutAvatar];

        const initStatuses = {};
        combined.forEach(p => {
          if (p.relation_status && p.relation_status !== 'none') {
            initStatuses[p.id] = { status: p.relation_status, isSender: p.relation_is_sender };
          }
        });
        setRelationStatuses(prev => ({ ...prev, ...initStatuses }));

        setAllPeople(combined);
        setAvatarStatus(statuses);
        setLoading(false);
      } catch (err) {
        if (axios.isCancel(err) || err.message === 'canceled') return;
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    };

    fetchPeople();
    return () => { cancelled = true; controller.abort(); };
  }, [profileType]);

  const getDisplayName = (person) => {
    if (person.nombre && person.apellido) return `${person.nombre} ${person.apellido}`;
    return person.name || "Usuario";
  };

  const getFirstName = (person) => {
    return person.nombre || person.name?.split(" ")[0] || "";
  };

  const getRole = (person) => {
    return person.cargo_nombre || person.role || person.profesion || "Profesional en Hitpoly";
  };

  const filteredPeople = React.useMemo(() => {
    if (!searchTerm.trim()) return allPeople;
    const term = searchTerm.toLowerCase().trim();
    return allPeople.filter(p =>
      getDisplayName(p).toLowerCase().includes(term) ||
      getRole(p).toLowerCase().includes(term) ||
      [p.country, p.city].filter(Boolean).join(' ').toLowerCase().includes(term)
    );
  }, [allPeople, searchTerm]);

  const visiblePeople = filteredPeople.slice(0, visibleCount);
  const hasMore = visibleCount < filteredPeople.length;

  const renderFriendButton = (person) => {
    if (Number(person.id) === Number(userIdRef.current)) return null;
    const rel = relationStatuses[person.id];
    if (!rel || rel.status === "none") {
      return (
        <Button size="small" variant="contained" sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.7rem', py: 0.4, boxShadow: 'none', minHeight: 28, bgcolor: '#0a66c2', minWidth: 90 }}
          startIcon={<UserPlus size={13} />}
          onClick={(e) => { e.stopPropagation(); handleFriendAction(person, "enviar"); }}>
          Conectar
        </Button>
      );
    }
    if (rel.status === "pending") {
      if (rel.isSender) {
        return (
          <Button size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.7rem', py: 0.4, minHeight: 28, borderColor: '#0a66c2', color: '#0a66c2', minWidth: 90 }}
            startIcon={<Check size={13} />}
            onClick={(e) => { e.stopPropagation(); handleFriendAction(person, "eliminar"); }}>
            Enviada
          </Button>
        );
      }
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="contained" sx={{ minWidth: 36, textTransform: 'none', borderRadius: 2, fontSize: '0.7rem', py: 0.4, boxShadow: 'none', minHeight: 28, bgcolor: '#0a66c2' }}
            onClick={(e) => { e.stopPropagation(); handleFriendAction(person, "aceptar"); }}>
            <Check size={13} />
          </Button>
          <Button size="small" variant="outlined" sx={{ minWidth: 36, textTransform: 'none', borderRadius: 2, fontSize: '0.7rem', py: 0.4, minHeight: 28, borderColor: '#e0e0e0', color: '#666' }}
            onClick={(e) => { e.stopPropagation(); handleFriendAction(person, "eliminar"); }}>
            <X size={13} />
          </Button>
        </Box>
      );
    }
    if (rel.status === "accepted") {
      return null;
    }
    return null;
  };

  if (loading) {
    return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <Skeleton variant="rounded" height={52} sx={{ mb: 3, borderRadius: 3 }} />
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))',
          gap: 2
        }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1.5 }}>
              <Skeleton variant="circular" width={64} height={64} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={100} height={20} sx={{ mb: 0.25 }} />
              <Skeleton variant="text" width={80} height={16} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={backHandler} sx={{ color: '#666', bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e8e8e8' } }}>
          <ArrowLeft size={20} />
        </IconButton>
        <Typography variant="h5" fontWeight="800" sx={{ fontSize: { xs: '1.1rem', md: '1.5rem' }, display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Users size={24} color="#0a66c2" /> {profileType === 'professional' ? 'Red de profesionales' : 'Red de usuarios'}
        </Typography>
        <Chip label={`${filteredPeople.length} ${profileType === 'professional' ? 'profesionales' : 'personas'}`} size="small" sx={{ fontWeight: 600, bgcolor: '#eef3f8', color: '#0a66c2', borderRadius: 2 }} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder={profileType === 'professional' ? "Buscar profesionales por nombre o especialidad..." : "Buscar personas por nombre o ubicación..."}
          size="small"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setVisibleCount(50); }}
          sx={{ maxWidth: 420, width: '100%' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search size={18} color="#999" /></InputAdornment>,
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setSearchTerm(""); setVisibleCount(50); }}>
                  <XCircle size={16} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { borderRadius: 3, bgcolor: '#f5f7fa', '& fieldset': { border: 'none' }, fontSize: '0.9rem' }
          }}
        />
      </Box>

      {visiblePeople.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, px: 2 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#eef3f8', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <Users size={36} color="#0a66c2" style={{ opacity: 0.5 }} />
          </Box>
          <Typography variant="h6" fontWeight="700" color="text.secondary" sx={{ mb: 0.5 }}>
            {searchTerm ? "Sin resultados" : (profileType === 'professional' ? "No hay profesionales disponibles" : "No hay personas disponibles")}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {searchTerm ? "Intenta con otro término de búsqueda" : (profileType === 'professional' ? "Pronto habrá más profesionales en la red" : "Pronto habrá más personas en la red")}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
            gap: 2
          }}>
            {visiblePeople.map(person => {
              const interestsArr = person.interests ? person.interests.split('||').filter(Boolean) : [];
              const shownInterests = interestsArr.slice(0, 3);
              return (
              <Card key={person.id}
                onMouseEnter={(e) => handleMouseEnter(e, person)}
                onMouseLeave={handleMouseLeave}
                sx={{
                  p: 1.5, borderRadius: 3, border: '1px solid #eef3f8',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  bgcolor: '#ffffff', cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', textAlign: 'center',
                  '&:hover': {
                    borderColor: '#0a66c2',
                    boxShadow: '0 8px 25px rgba(10, 102, 194, 0.12)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                <Box onClick={() => goToProfile(person)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 1 }}>
                  <Avatar
                    src={avatarStatus[person.id] ? person.avatar : undefined}
                    sx={{ width: 64, height: 64, mb: 1, border: '2px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', bgcolor: '#0a66c2', color: '#fff', fontWeight: 'bold', fontSize: '1.4rem' }}
                  >
                    {getFirstName(person).charAt(0)}
                  </Avatar>
                  <Typography variant="body2" fontWeight="800" color="#1a1a1a" sx={{ mb: 0.25, lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', width: '100%' }}>
                    {getDisplayName(person)}
                  </Typography>
                  {(person.country || person.city || person.personal_status) && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.25, opacity: 0.8 }}>
                      {[person.country, person.city].filter(Boolean).join(', ')}{(person.country || person.city) && person.personal_status ? ' · ' : ''}{person.personal_status || ''}
                    </Typography>
                  )}
                  {person.bio && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.6rem', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', width: '100%', px: 0.5 }}>
                      {person.bio}
                    </Typography>
                  )}
                  {shownInterests.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap', justifyContent: 'center', mt: 0.5 }}>
                      {shownInterests.map((interest, i) => (
                        <Chip key={i} label={interest} size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#eef3f8', color: '#0a66c2', '& .MuiChip-label': { px: 0.5 } }} />
                      ))}
                    </Box>
                  )}
                </Box>
                {renderFriendButton(person)}
              </Card>
              );
            })}
          </Box>

          {hasMore && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => setVisibleCount(p => p + 50)}
                sx={{ textTransform: 'none', borderRadius: 3, px: 4, borderColor: '#0a66c2', color: '#0a66c2', fontWeight: 600, py: 0.75, '&:hover': { borderColor: '#0a66c2', bgcolor: '#eef3f8' } }}
              >
                Mostrar más ({filteredPeople.length - visiblePeople.length} restantes)
              </Button>
            </Box>
          )}
        </>
      )}

      {hoverPerson && hoverAnchor && (
        <PersonHoverCard
          person={hoverPerson}
          anchorEl={hoverAnchor}
          currentUserId={userIdRef.current}
          relationStatus={relationStatuses[hoverPerson.id]}
          onClose={() => { setHoverPerson(null); setHoverAnchor(null); }}
          onMouseEnter={() => { clearTimeout(hoverTimer.current); }}
          onMouseLeave={handleMouseLeave}
          onFriendAction={(action) => handleFriendAction(hoverPerson, action)}
          onNavigateProfile={() => { setHoverPerson(null); goToProfile(hoverPerson); }}
          onNavigateMessage={() => {
            setHoverPerson(null);
            const nombre = hoverPerson.nombre || hoverPerson.name?.split(" ")[0] || "";
            const apellido = hoverPerson.apellido || hoverPerson.name?.split(" ").slice(1).join(" ") || "";
            window.postMessage({
              type: 'OPEN_FLOATING_CHAT',
              partner: {
                id: hoverPerson.id,
                name: `${nombre} ${apellido}`.trim(),
                avatar: hoverPerson.avatar || '',
                type: 'professional'
              }
            }, '*');
          }}
        />
      )}
    </Box>
  );
};

const PeopleNetworkPageWithLayout = (props) => {
  if (props.onBack) return <PeopleNetworkPage {...props} />;
  return (
    <DashboardLayout disablePadding={true}>
      <PeopleNetworkPage {...props} />
    </DashboardLayout>
  );
};
export default PeopleNetworkPageWithLayout;
